import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Baby, FileText, Users, TrendingUp, AlertTriangle, Cake, Wallet, Building2, ShieldAlert, Link as LinkIcon, CheckCircle2, LayoutGrid } from "lucide-react";
import { Link } from "react-router-dom";
import BKRPushBanner from "@/components/BKRPushBanner";
import OverstaffedWidget from "@/components/OverstaffedWidget";
import RebalancePanel from "@/components/RebalancePanel";
import CoachHistorie from "@/components/CoachHistorie";
import InvitationsStatsCard from "@/components/InvitationsStatsCard";
import ActieCentrum from "@/components/ActieCentrum";
import { BKRRevealPopover } from "@/pages/rooster/BKRRevealPopover";

function StatCard({ label, value, icon: Icon, tone = "sage", helper, testid }) {
  const toneClasses = {
    sage: "border-l-[color:var(--sage)]",
    terra: "border-l-[color:var(--terracotta)]",
    warn: "border-l-amber-500",
    ok: "border-l-emerald-500",
  }[tone];
  return (
    <Card className={`p-4 sm:p-6 border-l-4 ${toneClasses} bg-white shadow-none hover:shadow-md transition-shadow`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wider font-medium">{label}</div>
          <div className="font-display text-2xl sm:text-3xl tracking-tight text-neutral-900 mt-1.5 sm:mt-2 truncate">{value}</div>
          {helper && <div className="text-[11px] sm:text-xs text-neutral-500 mt-1">{helper}</div>}
        </div>
        <Icon className="h-5 w-5 text-neutral-300 shrink-0" strokeWidth={1.8} />
      </div>
    </Card>
  );
}

const BKR_TONE = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  alert: "bg-red-50 text-red-700 border-red-200",
  overstaffed: "bg-purple-50 text-purple-800 border-purple-300",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [certs, setCerts] = useState(null); // WEBSTER KOV — VOG/EHBO widget
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === "pedagogisch" || user?.role === "ouder") { setLoading(false); return; }
    const fetchStats = () => api.get("/dashboard/stats")
      .then(r => setStats(r.data))
      .catch(() => { /* stil bij transient errors, refresh probeert opnieuw */ })
      .finally(() => setLoading(false));
    const fetchCerts = () => api.get("/dashboard/certificaten-widget")
      .then(r => setCerts(r.data))
      .catch(() => { /* stil */ });
    fetchStats();
    fetchCerts();
    // Auto-refresh elke 60 seconden zodat aankomsten/vertrekken in de BKR-strip
    // direct zichtbaar zijn zonder page-reload. Refresh pauzeert automatisch als
    // het tab niet visible is (voorkomt onnodige requests in achtergrond-tabs).
    const tick = () => {
      if (document.visibilityState === "visible") { fetchStats(); fetchCerts(); }
    };
    const id = window.setInterval(tick, 60_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [user?.role]);

  // Pedagogisch heeft geen dashboard-toegang meer — stuur ze terug naar /groep.
  if (user?.role === "pedagogisch") return <Navigate to="/groep" replace />;
  if (user?.role === "ouder") return <Navigate to="/portaal" replace />;

  return (
    <div>
      <PageHeader
        title={`Welkom terug, ${user?.name?.split(" ")[0] || ""}`}
        subtitle="Realtime overzicht van je organisatie — contracten, bezetting, financieel en compliance in één blik."
        testid="dashboard-header"
      />

      <BKRPushBanner />
      <ActieCentrum />
      {/* Management-only widgets: financieel/pedagogisch/ouder role has no
          permission for invitations/rebalance/BKR-alerts and would get 403s */}
      {/* Alleen directie/platform mag invitation-stats + consistency zien.
          Locatiemanager mag OverstaffedWidget/RebalancePanel wél zien
          (roostering/BKR), maar invitations en consistency niet. */}
      {["superadmin", "platform_admin"].includes(user?.role) && (
        <>
          <InvitationsStatsCard />
          <ConsistencyAuditTile />
        </>
      )}
      {["superadmin", "platform_admin", "locatiemanager"].includes(user?.role) && (
        <>
          <OverstaffedWidget />
          <RebalancePanel weken={4} />
        </>
      )}

      {loading || !stats ? (
        <div className="text-sm text-neutral-500">Dashboard wordt geladen…</div>
      ) : (
        <>
          {/* Top KPIs — Omzet YTD alleen voor rollen met financiële toegang */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <StatCard testid="stat-children" label="Kinderen in beheer" value={stats.total_children} icon={Baby} tone="sage" />
            <StatCard testid="stat-contracts" label="Actieve contracten" value={stats.active_contracts} icon={FileText} tone="sage" helper={`${stats.starts_this_month} starts deze maand`} />
            <StatCard testid="stat-staff" label="Medewerkers actief" value={stats.staff} icon={Users} tone="terra" />
            {["superadmin", "platform_admin", "financieel"].includes(user?.role) && (
              <StatCard testid="stat-revenue" label="Omzet YTD" value={`€ ${stats.omzet_ytd.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`} icon={TrendingUp} tone="ok" />
            )}
          </div>

          {/* Live BKR-strip — vandaag per groep, zonder naar Roostering te hoeven */}
          {stats.bkr && stats.bkr.length > 0 && <LiveBkrStrip items={stats.bkr} />}

          {/* WEBSTER KOV — PM-Overzicht Widget: verlopen/verlopende VOG + EHBO. */}
          {certs && ((certs.vog.verlopen + certs.vog.verloopt_30d + certs.ehbo.verlopen + certs.ehbo.verloopt_30d) > 0) && (
            <CertificatenWidget data={certs} />
          )}

          {/* Mentor-waarschuwing — alleen tonen als er kinderen zonder mentor zijn */}
          {(user?.role === "superadmin" || user?.role === "locatiemanager") && <MentorWarningCard />}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Aandachtspunten */}
            <Card className="p-4 sm:p-6 col-span-1 lg:col-span-2 bg-white shadow-none">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 text-[color:var(--terracotta)]" />
                <h3 className="font-display text-lg tracking-tight">Aandachtspunten</h3>
              </div>
              <div className="space-y-3">
                {["superadmin", "platform_admin", "financieel"].includes(user?.role) && (
                  <>
                    <AttentionRow
                      icon={<Wallet className="h-4 w-4" />}
                      label="Openstaande facturen"
                      value={`€ ${stats.openstaand.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`}
                      hint={`${stats.overtijd} overtijd`}
                      tone="warn"
                      testid="attn-open-invoices"
                    />
                    <AttentionRow
                      icon={<FileText className="h-4 w-4" />}
                      label="Ouders zonder machtiging"
                      value={stats.parents_no_mach}
                      hint="Nodig voor automatische incasso"
                      tone={stats.parents_no_mach > 0 ? "warn" : "ok"}
                      testid="attn-parents-no-mach"
                    />
                  </>
                )}
                <AttentionRow
                  icon={<FileText className="h-4 w-4" />}
                  label="Contracten eindigen deze maand"
                  value={stats.ends_this_month}
                  hint="Bevestig verlenging of einde"
                  tone={stats.ends_this_month > 0 ? "warn" : "ok"}
                  testid="attn-ending-contracts"
                />
              </div>

              <div className="mt-8 pt-6 border-t border-neutral-100">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="h-4 w-4 text-[color:var(--sage)]" />
                  <h4 className="font-display text-base">BKR-controle — indicatief</h4>
                </div>
                <div className="text-[11px] text-neutral-500 mb-3">
                  Beroepskracht-kindratio per groep, berekend op basis van leeftijd van de kinderen. Dit is een <strong>hulpmiddel bij controle</strong>, geen wettelijke garantie. Voor definitieve BKR-check gebruik de officiële <a href="https://www.1ratio.nl" target="_blank" rel="noopener noreferrer" className="text-[color:var(--sage)] underline">SZW-rekentool op 1ratio.nl</a>.
                </div>
                <div className="space-y-2">
                  {stats.bkr.length === 0 && <div className="text-sm text-neutral-500">Geen groepen zichtbaar.</div>}
                  {stats.bkr.map((b, i) => (
                    <div key={`${b.type}-${b.group_id || b.location_id}-${i}`} className="flex items-center justify-between py-2" data-testid={`bkr-${b.group_id || b.location_id}`}>
                      <div>
                        <div className="text-sm font-medium text-neutral-800 flex items-center gap-2">
                          {b.name}
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-neutral-50 text-neutral-600 border-neutral-200">
                            {b.type} · {b.scope}
                          </Badge>
                        </div>
                        <div className="text-xs text-neutral-500">
                          {b.kinderen} kinderen · {b.medewerkers} medewerker(s) · vereist ≥ {b.vereist}
                          {b.leeftijd_afwijking > 0 && <span className="text-amber-700 ml-1">· {b.leeftijd_afwijking} kind(eren) buiten leeftijdsbereik</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <BKRRevealPopover bkr={b} testid={`dashboard-bkr-reveal-${b.group_id || b.location_id}`} />
                        <Badge className={`border ${BKR_TONE[b.status]}`} variant="outline">
                          {b.status === "ok" ? "OK" : b.status === "warning" ? "Krap" : "Onderbezet"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Compliance — EHBO / Diploma waarschuwingen */}
            {(user?.role === "superadmin" || user?.role === "locatiemanager") && (
              <ComplianceWidget />
            )}

            {/* Verjaardagen */}
            <Card className="p-4 sm:p-6 bg-white shadow-none">
              <div className="flex items-center gap-2 mb-4">
                <Cake className="h-4 w-4 text-[color:var(--terracotta)]" />
                <h3 className="font-display text-lg tracking-tight">Verjaardagen komende 7 dagen</h3>
              </div>
              <div className="space-y-3" data-testid="birthdays-list">
                {stats.upcoming_birthdays.length === 0 && (
                  <div className="text-sm text-neutral-500">Deze week geen jarigen.</div>
                )}
                {stats.upcoming_birthdays.map(b => (
                  <div key={b.id} className="flex items-center gap-3 p-3 rounded-md bg-neutral-50 border border-neutral-100">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color:var(--terracotta)] text-white font-semibold text-sm">
                      {b.leeftijd}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-800 truncate">{b.voornaam} {b.achternaam}</div>
                      <div className="text-xs text-neutral-500">{new Date(b.datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          {/* CoachHistorie roept /api/groups/users/bkr-reallocaties aan;
              financieel heeft daar geen rechten. Verberg voor die rol. */}
          {["superadmin", "platform_admin", "locatiemanager"].includes(user?.role) && <CoachHistorie />}
        </>
      )}
    </div>
  );
}

function AttentionRow({ icon, label, value, hint, tone, testid }) {
  const toneClass = {
    warn: "bg-amber-50 text-amber-700",
    ok: "bg-emerald-50 text-emerald-700",
  }[tone];
  return (
    <div className="flex items-center gap-4 p-3 rounded-md border border-neutral-100" data-testid={testid}>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${toneClass}`}>{icon}</div>
      <div className="flex-1">
        <div className="text-sm font-medium text-neutral-800">{label}</div>
        <div className="text-xs text-neutral-500">{hint}</div>
      </div>
      <div className="font-display text-xl text-neutral-900">{value}</div>
    </div>
  );
}


/** Dashboard-widget: laat EHBO-verlopen en missende diploma's zien met CTA naar de medewerker-pagina. */
function ComplianceWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/medewerkers/compliance-alerts")
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const { verlopen, bijna_verlopen, geen_diploma, geen_ehbo } = data.alerts;
  const kritiek = verlopen.length + geen_diploma.length;
  const aandacht = bijna_verlopen.length;
  const info = geen_ehbo.length;

  return (
    <Card className="p-4 sm:p-6 bg-white shadow-none" data-testid="compliance-widget">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className={`h-4 w-4 ${kritiek > 0 ? "text-red-600" : aandacht > 0 ? "text-amber-600" : "text-emerald-600"}`} />
          <h3 className="font-display text-lg tracking-tight">Compliance-check</h3>
        </div>
        <Link to="/app/medewerkers" className="text-xs text-[color:var(--sage)] hover:underline flex items-center gap-1" data-testid="compliance-go-medewerkers">
          Beheer <LinkIcon className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="compliance-summary">
        <div className={`p-2 rounded-md text-center ${kritiek > 0 ? "bg-red-50 text-red-800 border border-red-200" : "bg-neutral-50 text-neutral-500 border border-neutral-200"}`}>
          <div className="text-2xl font-bold tabular-nums">{kritiek}</div>
          <div className="text-[10px] uppercase tracking-widest">kritiek</div>
        </div>
        <div className={`p-2 rounded-md text-center ${aandacht > 0 ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-neutral-50 text-neutral-500 border border-neutral-200"}`}>
          <div className="text-2xl font-bold tabular-nums">{aandacht}</div>
          <div className="text-[10px] uppercase tracking-widest">bijna</div>
        </div>
        <div className="p-2 rounded-md text-center bg-neutral-50 text-neutral-600 border border-neutral-200">
          <div className="text-2xl font-bold tabular-nums">{info}</div>
          <div className="text-[10px] uppercase tracking-widest">geen EHBO</div>
        </div>
      </div>

      {kritiek === 0 && aandacht === 0 && (
        <div className="text-xs text-neutral-500 py-3 text-center" data-testid="compliance-clean">
          Geen actuele waarschuwingen · geen verlopen certificaten in de komende {data.horizon_dagen} dagen.
        </div>
      )}

      <div className="space-y-1.5 max-h-64 overflow-y-auto">
        {verlopen.map((it) => (
          <ComplianceRow key={it.id + it.certificaat} tone="red" title={it.naam} sub={`${it.certificaat} verlopen sinds ${it.geldig_tot} (${Math.abs(it.dagen)}d)`} />
        ))}
        {geen_diploma.map((it) => (
          <ComplianceRow key={it.id + "diploma"} tone="red" title={it.naam} sub={it.reden} />
        ))}
        {bijna_verlopen.map((it) => (
          <ComplianceRow key={it.id + it.certificaat} tone="amber" title={it.naam} sub={`${it.certificaat} verloopt op ${it.geldig_tot} (nog ${it.dagen}d)`} />
        ))}
      </div>

      {info > 0 && kritiek + aandacht === 0 && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Aanbeveling — geen EHBO</div>
          <div className="text-xs text-neutral-600">
            {geen_ehbo.slice(0, 4).map(m => m.naam).join(", ")}
            {geen_ehbo.length > 4 && ` +${geen_ehbo.length - 4} meer`}
          </div>
        </div>
      )}
    </Card>
  );
}

function ComplianceRow({ tone, title, sub }) {
  const toneClass = tone === "red"
    ? "bg-red-50 border-red-200 text-red-900"
    : "bg-amber-50 border-amber-200 text-amber-900";
  return (
    <div className={`px-2.5 py-1.5 rounded border text-xs flex items-start gap-2 ${toneClass}`} data-testid={`compliance-row-${tone}`}>
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="font-medium truncate">{title}</div>
        <div className="text-[11px] opacity-90 truncate">{sub}</div>
      </div>
    </div>
  );
}



/** Compacte horizontale strip die per groep de live BKR-status toont. Klik → Roostering. */
function LiveBkrStrip({ items }) {
  const today = new Date();
  const dagNaam = ["Zondag", "Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag"][today.getDay()];
  const totKinderen = items.reduce((sum, b) => sum + (b.kinderen || 0), 0);
  const alerts = items.filter(b => b.status === "alert").length;

  return (
    <div className="mb-6 sm:mb-8" data-testid="live-bkr-strip">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <LayoutGrid className="h-4 w-4 text-[color:var(--sage)] shrink-0" />
          <h3 className="font-display text-lg tracking-tight">Live BKR — vandaag ({dagNaam})</h3>
          {alerts > 0 && (
            <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" data-testid="bkr-strip-alerts">
              {alerts} onderbezet
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-xs text-neutral-500">
            {totKinderen} kinderen totaal
          </div>
          <Link to="/app/rooster" className="text-xs text-[color:var(--sage)] hover:underline flex items-center gap-1" data-testid="bkr-strip-goto">
            Rooster <LinkIcon className="h-3 w-3" />
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {items.map((b) => (
          <BkrChip key={`${b.group_id || b.location_id}-${b.scope}`} b={b} />
        ))}
      </div>
    </div>
  );
}

function BkrChip({ b }) {
  const isAlert = b.status === "alert";
  const isWarn = b.status === "warning";
  const tone = isAlert
    ? "bg-red-50 border-red-200"
    : isWarn
      ? "bg-amber-50 border-amber-200"
      : "bg-emerald-50 border-emerald-200";
  const numTone = isAlert
    ? "text-red-800"
    : isWarn
      ? "text-amber-800"
      : "text-emerald-800";
  const bkrLabel = isAlert
    ? "Onderbezet"
    : isWarn
      ? "Let op"
      : b.vereist === 0 ? "Geen kinderen" : "Op sterkte";

  return (
    <Link to="/app/rooster" className="block">
      <div className={`rounded-md border p-3 transition-shadow hover:shadow-md ${tone}`} data-testid={`bkr-chip-${b.group_id || b.location_id}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-neutral-800 truncate">{b.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {b.type} · {b.scope}
            </div>
          </div>
          {isAlert ? <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
            : isWarn ? <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              : <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
        </div>
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-2xl font-bold tabular-nums ${numTone}`}>
            {b.medewerkers}
            <span className="text-neutral-400 text-sm font-normal">/{Math.max(b.vereist, 1)}</span>
          </span>
          <span className="text-[11px] text-neutral-600">medewerkers</span>
        </div>
        <div className="flex items-center justify-between text-[11px] text-neutral-600">
          <span>
            <strong className="text-neutral-800">{b.kinderen}</strong> kinderen
          </span>
          <span className={`font-medium ${numTone}`}>{bkrLabel}</span>
        </div>
        {b.leeftijd_afwijking > 0 && (
          <div className="mt-1 text-[10px] text-amber-700">
            ⚠ {b.leeftijd_afwijking} kind(eren) buiten leeftijdsbereik
          </div>
        )}
      </div>
    </Link>
  );
}



/** Rode kaart die alleen verschijnt als er kinderen zonder mentor zijn.
 *  Klik → navigeer naar /app/kinderen met filter `mentor_missing=1`. */
function MentorWarningCard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get("/planning/mentor-warnings")
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); });
    return () => { mounted = false; };
  }, []);

  if (!data || data.count === 0) return null;

  const preview = (data.kinderen || []).slice(0, 6);
  return (
    <button
      type="button"
      onClick={() => navigate("/app/kinderen?mentor_missing=1")}
      data-testid="mentor-warning-card"
      className="w-full text-left mb-6 sm:mb-8 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 transition p-4 sm:p-5 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-md bg-red-600 text-white flex items-center justify-center shrink-0">
        <ShieldAlert className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-base text-red-800">
            {data.count} {data.count === 1 ? "kind heeft nog geen mentor" : "kinderen hebben nog geen mentor"}
          </div>
          <Badge variant="outline" className="bg-red-600 text-white border-red-700 text-[10px]">
            Actie vereist
          </Badge>
        </div>
        <div className="text-xs text-red-700/80 mt-1">
          Wijs een mentor toe bij start van de opvang — dit is een verplichting binnen de kinderopvang.
        </div>
        {preview.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="mentor-warning-list">
            {preview.map(k => (
              <span key={k.id} className="text-[11px] bg-white border border-red-200 rounded px-1.5 py-0.5 text-red-800">
                {k.voornaam} {k.achternaam}
              </span>
            ))}
            {data.count > preview.length && (
              <span className="text-[11px] text-red-700/80 self-center">
                +{data.count - preview.length} meer
              </span>
            )}
          </div>
        )}
      </div>
      <div className="text-xs text-red-700 shrink-0 self-center hidden sm:block">
        Bekijk alle →
      </div>
    </button>
  );
}


function ConsistencyAuditTile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!["superadmin", "locatiemanager"].includes(user?.role)) return;
    api.get("/consistency/latest").then(r => setData(r.data)).catch(() => setData(null));
  }, [user?.role]);
  if (!data || !["superadmin", "locatiemanager"].includes(user?.role)) return null;

  const anomalies = data.total_anomalies || 0;
  const tone = data.overall_pass ? "emerald" : anomalies < 5 ? "amber" : "red";
  const bg = tone === "emerald" ? "bg-emerald-50 border-emerald-200"
           : tone === "amber"   ? "bg-amber-50 border-amber-200"
           : "bg-red-50 border-red-200";
  const iconClr = tone === "emerald" ? "text-emerald-600"
                : tone === "amber"   ? "text-amber-600"
                : "text-red-600";
  const Icon = data.overall_pass ? CheckCircle2 : ShieldAlert;
  return (
    <div className={`mb-4 rounded-lg border ${bg} p-3 sm:p-4`} data-testid="audit-tile">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="audit-tile-btn">
        <div className="flex items-center gap-3 min-w-0">
          <Icon className={`h-5 w-5 ${iconClr} shrink-0`} />
          <div className="min-w-0">
            <div className="font-medium text-sm">
              Data-consistentie · {data.overall_pass ? "alles conform" : `${anomalies} afwijking${anomalies === 1 ? "" : "en"}`}
            </div>
            <div className="text-[11px] text-neutral-500">
              Laatste run: {data.generated_at ? new Date(data.generated_at).toLocaleString('nl-NL') : "—"} · bron: {data.source}
            </div>
          </div>
        </div>
        <Badge variant="outline" className={`${bg} ${iconClr} border-current`}>
          {data.overall_pass ? "OK" : `${anomalies}`}
        </Badge>
      </button>
      {open && (
        <div className="mt-3 pt-3 border-t border-current/10 space-y-1" data-testid="audit-tile-details">
          {(data.rules_summary || []).map(r => (
            <div key={r.rule_id} className="flex items-center justify-between text-xs" data-testid={`audit-rule-${r.rule_id}`}>
              <span>
                {r.passed ? "✅" : "⚠️"} Regel {r.rule_id}: {r.label}
              </span>
              <span className={r.passed ? "text-neutral-500" : "text-red-700 font-medium"}>
                {r.anomalies}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * WEBSTER KOV — CertificatenWidget
 *
 * Compact dashboard-tegel met tellingen verlopen/verlopende VOG en EHBO.
 * Doel: directie ziet meteen op het dashboard wie actie nodig heeft.
 * Klik-doorlink naar medewerker-detail (staf-pagina).
 */
function CertificatenWidget({ data }) {
  const vogTotaal = data.vog.verlopen + data.vog.verloopt_30d;
  const ehboTotaal = data.ehbo.verlopen + data.ehbo.verloopt_30d;
  const rowsVog = data.vog.medewerkers.slice(0, 4);
  const rowsEhbo = data.ehbo.medewerkers.slice(0, 4);
  return (
    <Card className="p-4 sm:p-5 bg-white shadow-none mb-6 sm:mb-8 border-l-4 border-l-red-500"
          data-testid="certificaten-widget">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">
          Certificaten · directe actie nodig
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* VOG kolom */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-bold text-neutral-900">VOG</span>
            {data.vog.verlopen > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 font-bold">
                {data.vog.verlopen} verlopen
              </span>
            )}
            {data.vog.verloopt_30d > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                {data.vog.verloopt_30d} ≤30d
              </span>
            )}
            {vogTotaal === 0 && <span className="text-[11px] text-emerald-700">✓ alles in orde</span>}
          </div>
          {rowsVog.length > 0 && (
            <div className="space-y-1">
              {rowsVog.map(m => {
                const isVerlopen = m.status === "verlopen";
                return (
                  <div key={m.id}
                       className={`flex items-center justify-between text-[11px] py-1 px-2 rounded ${isVerlopen ? "bg-red-50" : "bg-amber-50"}`}
                       data-testid={`cert-vog-row-${m.id}`}>
                    <span className="font-medium text-neutral-800 truncate">{m.naam}</span>
                    <span className={`tabular-nums font-medium ${isVerlopen ? "text-red-800" : "text-amber-800"}`}>
                      {isVerlopen ? `${Math.abs(m.days_left)}d over` : `nog ${m.days_left}d`}
                    </span>
                  </div>
                );
              })}
              {data.vog.medewerkers.length > 4 && (
                <div className="text-[10px] text-neutral-500 italic">+ {data.vog.medewerkers.length - 4} meer</div>
              )}
            </div>
          )}
        </div>
        {/* EHBO kolom */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-bold text-neutral-900">EHBO</span>
            {data.ehbo.verlopen > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 font-bold">
                {data.ehbo.verlopen} verlopen
              </span>
            )}
            {data.ehbo.verloopt_30d > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                {data.ehbo.verloopt_30d} ≤30d
              </span>
            )}
            {ehboTotaal === 0 && <span className="text-[11px] text-emerald-700">✓ alles in orde</span>}
          </div>
          {rowsEhbo.length > 0 && (
            <div className="space-y-1">
              {rowsEhbo.map(m => {
                const isVerlopen = m.status === "verlopen";
                return (
                  <div key={m.id}
                       className={`flex items-center justify-between text-[11px] py-1 px-2 rounded ${isVerlopen ? "bg-red-50" : "bg-amber-50"}`}
                       data-testid={`cert-ehbo-row-${m.id}`}>
                    <span className="font-medium text-neutral-800 truncate">{m.naam}</span>
                    <span className={`tabular-nums font-medium ${isVerlopen ? "text-red-800" : "text-amber-800"}`}>
                      {isVerlopen ? `${Math.abs(m.days_left)}d over` : `nog ${m.days_left}d`}
                    </span>
                  </div>
                );
              })}
              {data.ehbo.medewerkers.length > 4 && (
                <div className="text-[10px] text-neutral-500 italic">+ {data.ehbo.medewerkers.length - 4} meer</div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="text-[10px] text-neutral-500 mt-3 pt-3 border-t border-neutral-100">
        Verlopen VOG betekent wettelijk werkverbod. Directie ontvangt ook een dagelijkse e-mail-samenvatting.
      </div>
    </Card>
  );
}

